import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { hotComments } from '@/mocks/gossip';
import SectionHeading from '@/pages/home/components/SectionHeading';

const REACTIONS = [
  { id: 'r1', label: '这瓜保熟', count: 842 },
  { id: 'r2', label: '蹲一个后续', count: 671 },
  { id: 'r3', label: '反转预警', count: 529 },
  { id: 'r4', label: '别上升家人', count: 1180 },
];

function toneClass(tone: string): string {
  switch (tone) {
    case 'accent':
      return 'bg-accent-500 text-accent-950';
    case 'secondary':
      return 'bg-secondary-500 text-background-50';
    default:
      return 'bg-primary-500 text-background-50';
  }
}

export default function CommentZone() {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [reactions, setReactions] = useState<Record<string, boolean>>({});

  const handlePost = () => {
    if (!draft.trim()) {
      setNotice('先写点什么再发吧，评论区在等你的看法。');
      return;
    }
    setNotice('这里是体验版评论区，内容只在本地展示，不会真正发出去。');
  };

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleReaction = (id: string) => {
    setReactions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="talk" className="border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="05 / Talk"
          title="热评现场"
          desc="吃瓜不只是看，还得聊。这里是今天讨论最热闹的角落，理性发言，开心围观。"
        />

        <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-10">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                  我
                </span>
                <div className="flex-1">
                  <textarea
                    value={draft}
                    maxLength={500}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      if (notice) setNotice('');
                    }}
                    rows={3}
                    placeholder="今天这口瓜，你怎么看？（文明吃瓜，别带节奏）"
                    className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 p-3 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-foreground-400">{draft.length}/500</span>
                    <button
                      type="button"
                      onClick={handlePost}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      发布看法
                    </button>
                  </div>
                  {notice ? <p className="mt-2 text-xs text-accent-700">{notice}</p> : null}
                </div>
              </div>
            </div>

            <ul className="mt-6 space-y-4">
              {hotComments.map((comment, i) => (
                <Reveal key={comment.id} delay={i * 50}>
                  <li className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${toneClass(comment.tone)}`}>
                      {comment.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-900">{comment.user}</span>
                        <span className="text-[11px] text-foreground-400">{comment.time}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                      <div className="mt-3 flex items-center gap-5">
                        <button
                          type="button"
                          onClick={() => toggleLike(comment.id)}
                          className={`flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
                            liked[comment.id] ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                          }`}
                        >
                          <i className={`${liked[comment.id] ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`} aria-hidden="true"></i>
                          {comment.likes + (liked[comment.id] ? 1 : 0)}
                        </button>
                        <button type="button" className="flex cursor-pointer items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-secondary-700">
                          <i className="ri-chat-1-line text-sm" aria-hidden="true"></i>
                          {comment.replies} 条回复
                        </button>
                      </div>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">你站哪一边？</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">点一下，看看有多少人和你想法一样。</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {REACTIONS.map((reaction) => (
                  <button
                    key={reaction.id}
                    type="button"
                    onClick={() => toggleReaction(reaction.id)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      reactions[reaction.id]
                        ? 'border-accent-500 bg-accent-500 text-accent-950'
                        : 'border-background-300/70 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-800'
                    }`}
                  >
                    {reaction.label} · {reaction.count + (reactions[reaction.id] ? 1 : 0)}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-alarm-warning-line text-base text-primary-600" aria-hidden="true"></i>
                <h3 className="font-heading text-base font-bold text-foreground-950">吃瓜公约</h3>
              </div>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                <li>不扒素人、不上升家人，只聊公开信息。</li>
                <li>没实锤之前，先别下结论。</li>
                <li>尊重每一位当事人，也尊重你自己。</li>
              </ul>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h3 className="font-heading text-base font-bold text-primary-800">有独家料？</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-primary-700/80">
                手上有截图、有线索、有时间线？交给我们，让更多人看到真相。
              </p>
              <button
                type="button"
                className="mt-3 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line text-sm" aria-hidden="true"></i>
                我要爆料
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}